import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import {
  Briefcase,
  ChevronRight,
  AlertCircle,
  CalendarDays,
  Clock,
  Building2,
  Loader2,
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CollegeCalendar.css';

const localizer = momentLocalizer(moment);

const CollegeCalendar = ({ onSwitchToJobs }) => {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/posted-jobs/${userId}`
        );
        const jobs = response.data;
        const calendarEvents = [];

        jobs.forEach((job) => {
          if (job.applyByDate) {
            calendarEvents.push({
              id: job._id,
              title: `Deadline: ${job.role}`,
              company: job.companyName,
              start: new Date(job.applyByDate),
              end: new Date(job.applyByDate),
              allDay: true,
              type: 'deadline',
            });
          }

          if (job.dateOfVisit) {
            calendarEvents.push({
              id: job._id + '_visit',
              title: `Drive: ${job.companyName}`,
              company: job.companyName,
              start: new Date(job.dateOfVisit),
              end: new Date(job.dateOfVisit),
              allDay: true,
              type: 'visit',
            });
          }
        });

        setEvents(calendarEvents);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleNavigate = (newDate) => setDate(newDate);
  const handleViewChange = (newView) => setView(newView);

  const eventStyleGetter = (event) => {
    if (event.type === 'deadline') {
      return {
        style: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#dc2626',
          border: 'none',
          borderLeft: '4px solid #ef4444',
          borderRadius: '6px',
          fontSize: '0.8rem',
          fontWeight: '600',
          padding: '3px 8px',
          fontFamily: 'inherit',
        },
      };
    }
    return {
      style: {
        backgroundColor: 'rgba(79, 110, 247, 0.1)',
        color: '#4f6ef7',
        border: 'none',
        borderLeft: '4px solid #4f6ef7',
        borderRadius: '6px',
        fontSize: '0.8rem',
        fontWeight: '600',
        padding: '3px 8px',
        fontFamily: 'inherit',
      },
    };
  };

  const upcomingEvents = events
    .filter((e) => new Date(e.start) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 5);

  const getDaysFromNow = (dateStr) => {
    const diff = moment(dateStr).diff(moment(), 'days');
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `${diff} days`;
  };

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="cc-root">
        <div className="cc-loading">
          <div className="cc-loading-card">
            <Loader2 className="cc-spin" size={36} />
            <span>Loading placement schedule…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-root">
      {/* Background blobs */}
      <div className="cc-bg-blob cc-blob-1" />
      <div className="cc-bg-blob cc-blob-2" />

      {/* ── Header ── */}
      <div className="cc-header">
        <div className="cc-header-left">
          <div className="cc-title-icon">
            <CalendarDays size={26} />
          </div>
          <div>
            <h1 className="cc-title">Placement Schedule</h1>
            <p className="cc-subtitle">
              Track company visits and application deadlines
            </p>
          </div>
        </div>

        <div className="cc-header-stats">
          <div className="cc-mini-stat">
            <span className="cc-mini-value">
              {events.filter((e) => e.type === 'visit').length}
            </span>
            <span className="cc-mini-label">Drives</span>
          </div>
          <div className="cc-mini-stat cc-mini-red">
            <span className="cc-mini-value">
              {events.filter((e) => e.type === 'deadline').length}
            </span>
            <span className="cc-mini-label">Deadlines</span>
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="cc-legend">
        <span className="cc-legend-item">
          <span className="cc-legend-dot cc-dot-blue" />
          Campus Drive
        </span>
        <span className="cc-legend-item">
          <span className="cc-legend-dot cc-dot-red" />
          Application Deadline
        </span>
      </div>

      {/* ── Layout ── */}
      <div className="cc-layout">
        {/* LEFT: Calendar */}
        <div className="cc-calendar-card">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 620 }}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            date={date}
            view={view}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            selectable
            popup
          />
        </div>

        {/* RIGHT: Sidebar */}
        <div className="cc-sidebar">
          {/* Upcoming Events Card */}
          <div className="cc-agenda-card">
            <div className="cc-agenda-header">
              <div className="cc-agenda-icon">
                <AlertCircle size={16} />
              </div>
              <h3>Upcoming Events</h3>
            </div>

            <div className="cc-agenda-list">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((evt, idx) => (
                  <div
                    key={idx}
                    className={`cc-agenda-item ${evt.type === 'deadline' ? 'cc-item-deadline' : 'cc-item-visit'}`}
                  >
                    <div className="cc-agenda-date">
                      <span className="cc-date-month">
                        {moment(evt.start).format('MMM')}
                      </span>
                      <span className="cc-date-day">
                        {moment(evt.start).format('DD')}
                      </span>
                    </div>
                    <div className="cc-agenda-info">
                      <h4 className="cc-agenda-title">{evt.title}</h4>
                      <div className="cc-agenda-meta">
                        <span className="cc-agenda-company">
                          <Building2 size={12} /> {evt.company}
                        </span>
                        <span
                          className={`cc-agenda-tag ${evt.type === 'deadline' ? 'cc-tag-deadline' : 'cc-tag-visit'}`}
                        >
                          {evt.type === 'visit' ? 'Campus Drive' : 'Deadline'}
                        </span>
                      </div>
                      <span className="cc-agenda-countdown">
                        <Clock size={11} /> {getDaysFromNow(evt.start)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="cc-no-events">
                  <CalendarDays size={32} className="cc-no-events-icon" />
                  <p>No upcoming events</p>
                  <span>All caught up! 🎉</span>
                </div>
              )}
            </div>

            <div className="cc-agenda-footer">
              <button className="cc-view-all-btn" onClick={onSwitchToJobs}>
                <Briefcase size={14} />
                View All Postings
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegeCalendar;